import React, { useState, useEffect } from "react";
import { fetchTopics, generateAIQuestions } from "../api";
import { useNavigate } from "react-router-dom";

export default function TopicSidebar() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [difficulty, setDifficulty] = useState("easy");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function getTopics() {
      try {
        const data = await fetchTopics();
        const flat = data.map(d => d._id.topic);
        setTopics([...new Set(flat)]);
      } catch (e) {
        console.error("Failed to fetch topics", e);
      }
    }
    getTopics();
  }, []);

  useEffect(() => {
    if (query) {
      const lower = query.toLowerCase();
      const matches = topics.filter(t => t.toLowerCase().includes(lower));
      setFiltered(matches.slice(0, 5));
    } else {
      setFiltered([]);
    }
  }, [query, topics]);

  async function handleStart() {
    const topicToUse = selectedTopic || query;
    if (!topicToUse) return;

    setLoading(true);
    setError("");
    try {
      const questions = await generateAIQuestions(topicToUse, difficulty);
      
      // Store in sessionStorage for the practice page to pick up
      sessionStorage.setItem("tempQuestions", JSON.stringify(questions));
      sessionStorage.setItem("practiceConfig", JSON.stringify({ topic: topicToUse, difficulty }));
      
      navigate("/practice/session");
    } catch (e) {
      setError("AI generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="topic-sidebar desktop-only">
      <div className="card">
        <div className="card-title">Quick AI Practice</div>
        <p style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 12 }}>
          Search any topic to generate custom AI practice questions.
        </p>

        {/* Search Bar */}
        <div className="search-container" style={{ position: "relative", marginBottom: 16 }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search topic (e.g. Probability)"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedTopic("");
            }}
          />
          {filtered.length > 0 && !selectedTopic && (
            <div className="suggestions-list">
              {filtered.map(t => (
                <div
                  key={t}
                  className="suggestion-item"
                  onClick={() => {
                    setSelectedTopic(t);
                    setQuery(t);
                    setFiltered([]);
                  }}
                >
                  {t}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Difficulty Selection */}
        <div className="card-title" style={{ fontSize: 10, marginTop: 10 }}>Select Difficulty</div>
        <div className="difficulty-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 16 }}>
          {["easy", "medium", "hard"].map(d => (
            <button
              key={d}
              className={`filter-btn ${difficulty === d ? "active" : ""}`}
              style={{ fontSize: 10, padding: "6px 2px", textAlign: "center", textTransform: "capitalize" }}
              onClick={() => setDifficulty(d)}
            >
              {d === "easy" ? "Beginner" : d === "medium" ? "Mid" : "Hard"}
            </button>
          ))}
        </div>

        {error && <div style={{ fontSize: 11, color: "var(--red)", marginBottom: 10 }}>{error}</div>}

        <button
          className="btn btn-blue btn-block"
          onClick={handleStart}
          disabled={loading || (!query && !selectedTopic)}
        >
          {loading ? "Generating..." : "Start Practice"}
        </button>
      </div>

      <style>{`
        .topic-sidebar {
          width: 280px;
          position: sticky;
          top: 28px;
          height: fit-content;
          align-self: flex-start;
          margin-left: 20px;
        }
        .suggestions-list {
          position: absolute;
          top: 100%;
          left: 0; right: 0;
          background: white;
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          z-index: 100;
          box-shadow: var(--shadow-md);
          margin-top: 4px;
        }
        .suggestion-item {
          padding: 8px 12px;
          cursor: pointer;
          font-size: 13px;
        }
        .suggestion-item:hover {
          background: var(--blue-bg);
          color: var(--blue);
        }
      `}</style>
    </div>
  );
}
